import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { logout } from '../auth.js';
import { t, toggleLocale, locale } from '../i18n.js';
import { cycleTheme, resolvedTheme, theme } from '../theme.js';
import { formatSize } from '../format.js';
import { forgetUrls } from '../thumbs.js';
import {
  albums,
  axisOf,
  busy,
  destinationOf,
  groups,
  lastFolder,
  me,
  parentOf,
  reader,
  selection,
  session,
  view,
  viewer,
  ROOT,
  type Destination,
} from '../state.js';
import { narrow } from './media.js';
import { closeInspector, onRefresh, openView } from '../actions.js';
import {
  IconAuto,
  IconBack,
  IconLeave,
  IconMe,
  IconMoon,
  IconRefresh,
  IconShared,
  IconShield,
  IconStar,
  IconTrash,
  IconSpace,
  IconSun,
} from './icons.js';
import { Avatar, IconButton, ImportPromptDialog, Notice, OfflineBanner, ProcessingNotice } from './parts.js';
import {
  CollectionAudience,
  CollectionToolbar,
  CreationForm,
  SpaceToolbar,
  SpaceView,
  Tree,
} from './space.js';
import { AddMenu } from './add.js';
import { CollectionPicker } from './picker.js';
import { GroupToolbar, SharedView } from './shared.js';
import { MeView } from './me.js';
import { AdminView } from './admin.js';
import { TrashView } from './trash.js';
import { NewsView } from './news.js';
import { VERSION } from '../changelog.js';
import { Inspector } from './inspector.js';
import { Viewer } from './viewer.js';
import { Reader } from './reader.js';
import { Editor } from './editor.js';
import { MediaPicker } from './media-picker.js';

/**
 * La coque — **trois destinations, définies par la question qu'on se pose**.
 *
 * La V1 avait cinq onglets par *type* (posts, notes, médias, albums, documents) :
 * retrouver un document connu par son nom obligeait à savoir d'abord dans quel
 * silo il vivait. Ranger par question supprime ce préalable, et permet d'ajouter
 * un type sans ajouter d'onglet.
 *
 * **Trois destinations depuis 4.5, et non quatre.** *Moi* est un écran de
 * réglages — profil, stockage, appareils, corbeille — et lui donner un quart du
 * rail lui donnait le poids d'un lieu où l'on travaille. Il s'atteint par
 * l'avatar, là où toutes les applications le mettent.
 *
 * Rail à gauche, panneau contextuel, contenu, inspecteur. **Sous 52 rem, cette
 * mise en page n'existe plus** : le rail passe en barre basse, le panneau quitte
 * l'écran, et l'arborescence revient par une feuille — voir `PaneToggle`.
 */

const DESTINATIONS: readonly {
  readonly id: Destination;
  readonly key: string;
  readonly icon: (p: { size?: number }) => JSX.Element;
}[] = [
  { id: 'space', key: 'nav.space', icon: IconSpace },
  { id: 'shared', key: 'nav.shared', icon: IconShared },
];

function go(destination: Destination): void {
  switch (destination) {
    case 'space':
      // On revient **là où l'on était** : la destination porte deux axes, et
      // celui qu'on regardait est celui qu'on veut retrouver.
      return openView(
        axisOf(view.value) === 'all'
          ? { kind: 'files' }
          : { kind: 'folder', folder: lastFolder.value },
      );
    case 'shared':
      return openView({ kind: 'shared' });
  }
}

function Rail(): JSX.Element {
  const current = destinationOf(view.value);
  return (
    <nav class="rail" aria-label={t('app.name')}>
      {DESTINATIONS.map(({ id, key, icon: Icon }) => (
        <button
          key={id}
          type="button"
          class={current === id ? 'rail-item current' : 'rail-item'}
          data-testid={`nav-${id}`}
          aria-current={current === id ? 'page' : undefined}
          onClick={() => go(id)}
          // **La question est l'info-bulle, plus le sous-titre.** Posée sous
          // chaque libellé, elle occupait en permanence le tiers du rail pour une
          // phrase qu'on ne lit qu'une fois. Elle levait alors une ambiguïté —
          // celle entre « Mon espace » et « Mes fichiers » — qui n'existe plus :
          // les deux ne différaient que par l'axe, et l'axe a rejoint l'écran.
          title={t(`${key}.q`)}
        >
          <Icon />
          <span>{t(key)}</span>
        </button>
      ))}
    </nav>
  );
}

/**
 * L'icône du thème dit **ce qu'on a choisi**, pas ce qui est appliqué.
 *
 * Elle a **quitté la barre du haut** *(2026-08-16)* pour laisser sa place à
 * l'actualisation : le même réglage vivait déjà dans *Moi*, en toutes lettres et
 * avec ses trois valeurs, là où le bouton ne montrait que la suivante. Deux
 * contrôles pour un réglage, dont un ambigu, contre zéro pour aller chercher ce
 * que les autres ont écrit — l'arbitrage se posait de lui-même. Le composant reste
 * ici : c'est un bouton correct, et l'écran étroit pourra le reprendre s'il le
 * faut.
 */
export function ThemeButton(): JSX.Element {
  const choice = theme.value;
  const label =
    choice === 'system'
      ? t('header.theme.system')
      : choice === 'light'
        ? t('header.theme.light')
        : t('header.theme.dark');
  return (
    <IconButton testId="theme-toggle" label={label} onClick={cycleTheme}>
      {choice === 'system' ? <IconAuto /> : resolvedTheme() === 'dark' ? <IconMoon /> : <IconSun />}
    </IconButton>
  );
}

/**
 * **Aller chercher les nouveautés** — le geste qui n'existait nulle part.
 *
 * L'icône tourne pendant le drainage, et ce n'est pas une décoration : sans
 * retour visible, un appui sur un miroir déjà à jour ne produit **rien du tout**
 * à l'écran, et se lit comme un bouton mort. C'est le cas le plus fréquent.
 *
 * Il reste actif pendant l'attente (`aria-disabled` plutôt que `disabled`) et
 * ignore les appuis répétés : un bouton grisé dans une barre fixe fait croire à
 * un blocage, et c'est justement pendant qu'on attend qu'on rappuie.
 */
function RefreshButton(): JSX.Element {
  const running = busy.value === t('header.refreshing');
  return (
    <button
      type="button"
      class={running ? 'icon-btn spinning' : 'icon-btn'}
      data-testid="refresh"
      aria-label={t('header.refresh')}
      title={t('header.refresh')}
      aria-disabled={running}
      onClick={() => {
        if (!running) void onRefresh();
      }}
    >
      <IconRefresh />
    </button>
  );
}

/**
 * **Le menu de compte** — et la raison pour laquelle la barre du haut tient
 * désormais sur un téléphone.
 *
 * Elle alignait sept contrôles en `nowrap` : à 390 px, la page défilait
 * horizontalement et « Se déconnecter » vivait hors de l'écran. Ce n'était pas
 * un défaut d'esthétique — on ne pouvait plus se déconnecter, ni atteindre *Moi*,
 * ni l'administration.
 *
 * Tout ce qui concerne le compte passe donc derrière l'avatar : l'identité, le
 * stockage, la langue, *Moi*, l'administration et la déconnexion. C'est la forme
 * que tout le monde connaît, elle est identique sur les deux tailles d'écran, et
 * elle rend au passage un service que la barre plate ne rendait pas : **l'`uid`
 * n'est plus écrit en permanence dans le décor**. C'est une donnée de diagnostic
 * — on la recopie pour un rapport d'incident, on ne la lit jamais — et elle
 * occupait la place la plus chère de l'écran, sous l'alias.
 */
function AccountMenu(): JSX.Element | null {
  const profile = me.value;
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; right: number }>({ top: 56, right: 12 });
  const host = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (ev: MouseEvent): void => {
      if (!host.current?.contains(ev.target as Node)) setOpen(false);
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!profile) return null;

  const toggle = (): void => {
    if (open) return setOpen(false);
    const rect = button.current?.getBoundingClientRect();
    if (rect) setAt({ top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) });
    setOpen(true);
  };

  const goto = (kind: 'me' | 'admin' | 'trash' | 'news'): void => {
    setOpen(false);
    openView({ kind });
  };

  return (
    <div class="account-host" ref={host}>
      <button
        type="button"
        class="account"
        ref={button}
        data-testid="nav-account"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('header.account', { name: profile.alias ?? '—' })}
        onClick={toggle}
      >
        <Avatar uid={profile.uid} alias={profile.alias} rev={profile.avatarRev} />
        {/* L'alias reste **visible** tant qu'il y a la place : c'est lui qui dit
            « c'est bien mon compte », et le cacher derrière l'avatar sur un grand
            écran ferait chercher une information qui tient en deux mots. */}
        <span class="account-alias" data-testid="alias">
          {profile.alias ?? '—'}
        </span>
      </button>

      {open ? (
        <div
          class="account-menu"
          role="menu"
          data-testid="account-menu"
          style={{ top: `${at.top}px`, right: `${at.right}px` }}
        >
          <div class="identity">
            <Avatar uid={profile.uid} alias={profile.alias} rev={profile.avatarRev} />
            <span class="grow">
              <span class="card-title">{profile.alias ?? '—'}</span>
              <span class="mono muted account-uid" data-testid="uid" title={t('header.identifier')}>
                {profile.uid}
              </span>
            </span>
          </div>

          <div class="account-line" data-testid="storage">
            <span>{t('header.storage')}</span>
            <span>
              {formatSize(profile.storage.usedBytes)} / {formatSize(profile.storage.quotaBytes)}
            </span>
          </div>

          <hr />

          <button
            type="button"
            role="menuitem"
            data-testid="nav-me"
            aria-current={view.value.kind === 'me' ? 'page' : undefined}
            onClick={() => goto('me')}
          >
            <IconMe size={18} />
            <span>{t('nav.me')}</span>
          </button>

          {/* **La corbeille est ici, et pas dans le rail.** On n'y va qu'une
              fois, quand on regrette un geste : une destination l'aurait mise
              sur le chemin de tous les jours. Elle est en revanche **au-dessus**
              de la langue et du thème, qui sont des réglages — elle, c'est une
              seconde chance, et elle a une date limite. */}
          <button
            type="button"
            role="menuitem"
            data-testid="nav-trash"
            aria-current={view.value.kind === 'trash' ? 'page' : undefined}
            onClick={() => goto('trash')}
          >
            <IconTrash size={18} />
            <span>{t('trash.title')}</span>
          </button>

          {/* **Les nouveautés, sous la corbeille** : même famille — on y va une
              fois, par curiosité, jamais pour travailler. Le numéro de version
              est **sur le bouton** plutôt qu'en pied de menu : c'est la réponse à
              « qu'est-ce que j'ai, au juste ? », et elle se lit sans cliquer. */}
          <button
            type="button"
            role="menuitem"
            data-testid="nav-news"
            aria-current={view.value.kind === 'news' ? 'page' : undefined}
            onClick={() => goto('news')}
          >
            <IconStar size={18} />
            <span class="grow">{t('news.title')}</span>
            <span class="mono muted" data-testid="version">
              {VERSION}
            </span>
          </button>

          {/* **La porte de l'administration n'existe que pour les administrateurs.**
              Ce n'est pas la garde — `assertAdmin` relit `PROFILE.Role` à chaque
              appel, et c'est elle qui protège. Mais un bouton qu'on ne peut pas
              utiliser n'apprend rien : il fait chercher un droit qu'on n'a pas. */}
          {profile.isAdmin ? (
            <button
              type="button"
              role="menuitem"
              data-testid="nav-admin"
              aria-current={view.value.kind === 'admin' ? 'page' : undefined}
              onClick={() => goto('admin')}
            >
              <IconShield size={18} />
              <span>{t('admin.title')}</span>
            </button>
          ) : null}

          <button
            type="button"
            role="menuitem"
            data-testid="locale-toggle"
            onClick={() => {
              toggleLocale();
              setOpen(false);
            }}
          >
            <span class="locale-code">{locale.value.toUpperCase()}</span>
            <span>{t('header.language')}</span>
          </button>

          <hr />

          <button type="button" role="menuitem" data-testid="logout" onClick={signOut}>
            <IconLeave size={18} />
            <span>{t('app.signOut')}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * **La barre du haut porte le lieu, sur un écran étroit** *(2026-08-15)*.
 *
 * Le titre vivait sous la barre de commandes, sur une rangée à lui : une ligne
 * entière de hauteur — la ressource rare d'un téléphone — pour un mot qu'on ne
 * lit qu'en arrivant. Le retour d'usage le dit : « la ligne présentant le dossier
 * courant est mal placée car elle prend de la place dans la page ; dans
 * l'application cette info est mise dans la barre de titre ». Le retour, lui,
 * l'accompagne : posé dans la barre de commandes, il **poussait à la ligne
 * suivante** le choix des vignettes.
 *
 * Les deux montent donc dans la barre du haut, qui existe de toute façon et qui
 * a la place — c'est la marque qui cède, et elle ne dit rien qu'on ne sache déjà.
 * Sur un grand écran rien ne change : le titre y est un chemin cliquable, et un
 * chemin appartient à la colonne de contenu, pas à un bandeau global.
 */
export function TopBar(): JSX.Element {
  const profile = me.value;
  const compact = narrow.value;
  return (
    <header class="topbar">
      {compact ? (
        <>
          <BackButton />
          <Title compact />
        </>
      ) : (
        <span class="brand">
          {t('app.name')}
          <span class="brand-dot" aria-hidden="true">
            .
          </span>
        </span>
      )}
      <span class="grow" />
      {profile ? (
        <span class="storage" title={t('header.storage')}>
          {formatSize(profile.storage.usedBytes)} / {formatSize(profile.storage.quotaBytes)}
        </span>
      ) : null}
      <RefreshButton />
      <AccountMenu />
    </header>
  );
}

/**
 * **Le miroir ne survit pas à la session.** Il porte les noms de tous les
 * documents ; le laisser sur un poste partagé rendrait la déconnexion cosmétique.
 * La redirection n'est émise qu'après l'effacement — et les URL présignées
 * gardées en mémoire partent avec, puisqu'elles signent des octets privés.
 */
function signOut(): void {
  forgetUrls();
  const cache = session.cache;
  if (!cache) {
    void logout();
    return;
  }
  void cache
    .clear()
    .catch(() => undefined)
    .then(() => logout());
}

/**
 * **Le titre d'un dossier est son chemin, et son chemin se remonte.**
 *
 * Il était un texte. Sur un écran large, l'arborescence à gauche donnait la
 * sortie ; sous 52 rem elle n'existe pas, et un dossier ouvert devenait un
 * cul-de-sac — descendre était impossible, remonter aussi.
 *
 * Chaque segment d'ancêtre est donc un bouton, et le dernier n'en est pas un :
 * on y est déjà. Les segments portent leur barre oblique et ne sont séparés par
 * aucun blanc — mis bout à bout, ils redonnent exactement le chemin, pour l'œil
 * comme pour qui l'entend lire.
 */
function FolderTitle({ folder }: { readonly folder: string }): JSX.Element {
  const parts = folder.split('/').filter(Boolean);
  const last = parts.length - 1;
  return (
    <h1 class="view-title" data-testid="view-title">
      {parts.map((part, i) => {
        const path = `/${parts.slice(0, i + 1).join('/')}`;
        if (i === last) return <span key={path}>/{part}</span>;
        return (
          <button
            key={path}
            type="button"
            class="crumb"
            data-testid={`crumb-${path}`}
            onClick={() => openView({ kind: 'folder', folder: path })}
          >
            /{part}
          </button>
        );
      })}
    </h1>
  );
}

/**
 * **Le retour** — le geste qui manquait à tout écran de second niveau.
 *
 * Un groupe ouvert, une collection ouverte, un sous-dossier : trois écrans dont
 * on ne sortait que par le rail, c'est-à-dire en retournant à la *liste* et non
 * au niveau au-dessus. Sur un téléphone, le geste système ne faisait pas mieux —
 * il quittait le site, faute d'entrées d'historique (`navigation.ts`, corrigé
 * avec).
 *
 * Il **remonte d'un cran** (`parentOf`) plutôt que de défaire le dernier pas :
 * c'est déterministe, et cela reste juste quel que soit le chemin par lequel on
 * est arrivé. Le geste système, lui, garde son sens propre — les deux coexistent
 * dans toutes les applications, et il arrive qu'ils ne mènent pas au même endroit.
 */
function BackButton(): JSX.Element | null {
  const up = parentOf(view.value);
  if (!up) return null;
  return (
    <IconButton testId="nav-back" label={t('nav.back')} onClick={() => openView(up)}>
      <IconBack size={18} />
    </IconButton>
  );
}

/**
 * Le titre, **en deux formes pour deux endroits**.
 *
 * Dans la colonne de contenu, c'est un chemin dont chaque ancêtre se clique.
 * Dans la barre du haut d'un téléphone, c'est **le nom de l'endroit** et rien
 * d'autre : une seule ligne, partagée avec le compte, où un chemin de trois
 * segments s'écraserait en points de suspension pour finir par cacher justement
 * le segment courant — le seul qu'on ait besoin de lire. L'ancêtre n'y est pas
 * perdu : il est ce que la flèche de retour, posée juste à gauche, ramène.
 */
function Title({ compact = false }: { readonly compact?: boolean } = {}): JSX.Element {
  const v = view.value;
  if (v.kind === 'folder' && v.folder !== ROOT) {
    if (!compact) return <FolderTitle folder={v.folder} />;
    return (
      <h1 class="view-title" data-testid="view-title" title={v.folder}>
        {v.folder.slice(v.folder.lastIndexOf('/') + 1)}
      </h1>
    );
  }
  return (
    <h1 class="view-title" data-testid="view-title">
      {title()}
    </h1>
  );
}

function title(): string {
  const v = view.value;
  switch (v.kind) {
    case 'folder':
      return v.folder === ROOT ? t('space.root') : v.folder;
    case 'files':
      return t('space.axis.all');
    case 'collection':
      return (albums.value ?? []).find((a) => a.Aid === v.aid)?.Name ?? t('collection.title');
    case 'shared':
      return t('nav.shared');
    case 'group':
      return (groups.value ?? []).find((g) => g.gid === v.gid)?.name ?? t('nav.shared');
    case 'me':
      return t('me.title');
    case 'admin':
      return t('admin.title');
    case 'trash':
      return t('trash.title');
    case 'news':
      return t('news.title');
  }
}

/**
 * **L'arborescence sur un écran étroit** — et pourquoi elle n'a plus de bouton à
 * elle.
 *
 * Le panneau de gauche disparaît sous 52 rem. Il avait donc été doublé d'un
 * bouton « Dossiers » qui ouvrait une feuille — et ce bouton était le défaut :
 * posé à côté d'un segment portant *le même mot*, il désignait le panneau quand
 * son voisin désignait l'axe. Sur le mobile, il était même remplacé par les trois
 * barres horizontales d'un tiroir, c'est-à-dire l'endroit où personne ne cherche
 * un dossier.
 *
 * L'arborescence se déploie désormais depuis le segment *Dossiers* lui-même
 * (`LocationSwitch`), à toutes les largeurs. Le panneau de gauche reste ce qu'il
 * est sur un grand écran : un confort permanent, pas le seul chemin.
 */

/**
 * **La fiche en feuille, sur un écran étroit.**
 *
 * Le panneau lui-même ne change pas — c'est le même `Inspector`, avec sa propre
 * croix de fermeture. Ce qui change est **où il se pose** : une feuille par le
 * bas plutôt qu'un bloc en tête de page, parce qu'une information qui apparaît
 * hors du champ où l'on vient d'agir n'apparaît pas.
 *
 * Le voile ferme, comme partout ailleurs ; le clic **dans** la feuille ne ferme
 * pas, sans quoi cocher une étoile la refermerait.
 */
function InspectorSheet(): JSX.Element {
  const close = closeInspector;
  return (
    <div class="overlay" data-testid="inspector-sheet" onClick={close}>
      <div class="sheet sheet-tall" onClick={(ev) => ev.stopPropagation()}>
        <Inspector />
      </div>
    </div>
  );
}

export function Shell(): JSX.Element {
  const destination = destinationOf(view.value);
  // **L'arborescence appartient à *Mon espace*, et à ses deux axes.**
  //
  // Elle en avait été retirée sur « Mes fichiers » en 4.5, et à raison : le
  // panneau restait visible, mais cliquer un dossier **faisait quitter l'écran**
  // — une navigation qui change de destination sans le dire. Depuis que les deux
  // axes vivent dans la même destination, ce n'est plus vrai : cliquer un dossier
  // depuis *Tout* bascule d'axe, on reste où l'on est, et le panneau redevient ce
  // qu'il prétend être.
  const withPane = destination === 'space';
  // **La sélection suffit, et rien d'autre** *(2026-08-14)*.
  //
  // Le panneau était conditionné à une liste d'écrans — d'abord la destination,
  // ce qui excluait une collection ; puis trois vues nommées, ce qui excluait le
  // fil d'un groupe. Or on peut désormais demander « Détails » depuis la
  // visionneuse et le lecteur, donc depuis une photo ouverte à partir d'un fil de
  // groupe : la fiche ne s'ouvrait alors nulle part, et le geste restait sans
  // effet visible. Il n'y a **aucun écran où une sélection ne doive pas s'ouvrir**
  // — c'est `select()` qui décide qu'il y a quelque chose à montrer, et il ne
  // sélectionne que ce qui existe.
  const withInspector = Boolean(selection.value);
  // **Sous 52 rem, la fiche est une feuille et non une colonne.**
  //
  // Il n'y a pas la place d'une troisième colonne : elle passait donc au-dessus
  // du contenu, en haut de la page — c'est-à-dire hors du regard de quelqu'un qui
  // vient de toucher une tuile au milieu de l'écran. Le retour d'usage a été
  // exactement celui-là : « on ne les remarque même pas ». Une feuille modale se
  // remarque parce qu'elle **couvre**, et c'est ce que l'app fait déjà.
  //
  // **Et par-dessus une surcouche, quelle que soit la largeur** *(2026-08-14)* :
  // une photo ouverte occupe l'écran entier en `z-index: 50`, si bien qu'un
  // « Détails » demandé depuis la visionneuse remplissait une colonne **derrière**
  // elle — invisible, donc un geste sans effet. La feuille, elle, monte à 55 et se
  // pose sur la photo qu'elle décrit, ce qui est aussi le seul endroit où la lire
  // a du sens.
  const overSurface = viewer.value !== undefined || reader.value !== undefined;
  const inspectorAsSheet = withInspector && (narrow.value || overSurface);
  const layout = [
    'body',
    withPane ? 'with-pane' : '',
    withInspector && !inspectorAsSheet ? 'with-inspector' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div class="app-shell">
      <TopBar />
      <OfflineBanner />
      <div class={layout}>
        <Rail />
        {withPane ? (
          <aside class="pane">
            <Tree />
          </aside>
        ) : null}
        <main class="view">
          {/* **Les contrôles d'abord, le lieu ensuite** *(2026-08-09)*.
              Le titre et la barre partageaient une rangée, le titre à gauche :
              sa largeur change avec l'endroit — « Tout » fait quatre lettres,
              « /Photos/Tests » en fait treize —, si bien que **tous les boutons
              se déplaçaient à chaque bascule d'axe**. C'est le retour d'usage :
              « on devrait l'avoir en dessous du choix dossier vs tout et pas au
              dessus, sinon tous les boutons bougent quand on change de mode ».

              Une barre de commandes est un endroit qu'on vise à l'aveugle, et
              une cible qui se déplace selon ce qu'on regarde ne s'apprend
              jamais. Elle est donc sur sa propre rangée, à une position fixe, et
              le chemin — qui *décrit* et ne se clique qu'exceptionnellement,
              pour remonter — passe dessous.

              Le `<h1>` suit les contrôles dans le document autant qu'à l'écran :
              l'ordre de lecture et l'ordre de tabulation restent le même, ce
              qu'un simple `order` en CSS aurait rompu. */}
          {/* **Sous 52 rem, cette rangée n'est plus là** *(2026-08-15)*, non plus
              que la flèche qui la précédait : les deux vivent dans la barre du
              haut, où il y a la place. Ce qui reste ici est ce qui règle l'écran
              — l'axe et la présentation —, et rien qui le décrive. */}
          <div class="view-head">
            <div class="toolbar">
              {narrow.value ? null : <BackButton />}
              <SpaceToolbar />
              <CollectionToolbar />
              <GroupToolbar />
              <AddMenu />
            </div>
            {narrow.value ? null : <Title />}
          </div>
          {/* Les deux formulaires de création naissent du même « + », donc ils
              s'affichent au même endroit — sous la barre qui les a ouverts. */}
          <CreationForm />
          <Notice />
          {/* Le compte des dérivés en cours est **à côté** de `Notice`, pas dedans :
              il n'est ni une attente ni une erreur, et il doit pouvoir s'afficher
              en même temps que l'une ou l'autre. */}
          <ProcessingNotice />
          {/* **L'audience se lit avant le contenu** *(2026-08-16)* : elle est la
              seule chose qu'un partage ait de plus qu'un dossier, et le réglage
              qui l'accompagne était introuvable tant qu'il vivait en icône dans
              la barre. Le bloc est au-dessus de la grille, pas dedans — il décrit
              l'endroit, il n'en fait pas partie. */}
          <CollectionAudience />
          <Body />
        </main>
        {withInspector && !inspectorAsSheet ? <Inspector /> : null}
      </div>
      {/* Les surcouches vivent **au-dessus de la coque**, pas dedans : elles
          couvrent l'écran, et les imbriquer dans la colonne de contenu les
          aurait fait défiler avec lui. */}
      {inspectorAsSheet ? <InspectorSheet /> : null}
      <CollectionPicker />
      <ImportPromptDialog />
      <Viewer />
      <Reader />
      <Editor />
      {/* **Après l'éditeur, parce qu'il s'ouvre par-dessus lui** : c'est la seule
          surcouche du produit qui en couvre une autre, et l'ordre du document est
          ce qui décide laquelle est au-dessus. */}
      <MediaPicker />
    </div>
  );
}

function Body(): JSX.Element | null {
  switch (view.value.kind) {
    // **Les deux axes ont le même corps.** Ils portent les mêmes objets ; leur
    // donner deux rendus faisait dépendre de l'axe ce qui ne dépend que de
    // l'objet — les filtres hier, la présentation aujourd'hui.
    case 'folder':
    case 'files':
      return <SpaceView />;
    // Une collection se **regarde** de la même façon — même grille, mêmes
    // filtres, même présentation, même sélection, même inspecteur — et n'en
    // diffère que par ce qu'elle contient et par le fait qu'on puisse en retirer
    // un élément sans le supprimer. Elle avait son propre corps jusqu'au
    // 2026-08-14 ; c'était le dernier écran où le lieu décidait de la façon de
    // regarder.
    case 'collection':
      return <SpaceView />;
    case 'shared':
    case 'group':
      return <SharedView />;
    case 'me':
      return <MeView />;
    case 'admin':
      return <AdminView />;
    case 'trash':
      return <TrashView />;
    case 'news':
      return <NewsView />;
  }
}
